import { useEffect, useState } from 'react';
import Modal from '@/components/base/Modal';
import { formatMoney } from '@/lib/format';
import { friendlyError } from '@/lib/errors';
import { PAYMENT_LABELS, type PaymentMethod } from '@/lib/types';

interface CheckoutModalProps {
  open: boolean;
  onClose: () => void;
  total: number;
  currencySymbol: string;
  paymentMethods: PaymentMethod[];
  requireReceived: boolean;
  onConfirm: (paymentMethod: string, amountReceived: number, changeAmount: number) => Promise<void>;
  processing: boolean;
}

const METHOD_ICONS: Record<string, string> = {
  cash: 'ri-cash-line',
  card: 'ri-bank-card-line',
  transfer: 'ri-bank-line',
  mixed: 'ri-wallet-3-line',
  other: 'ri-more-line',
};

const round2 = (n: number) => Math.round(n * 100) / 100;

export default function CheckoutModal({
  open,
  onClose,
  total,
  currencySymbol,
  paymentMethods,
  requireReceived,
  onConfirm,
  processing,
}: CheckoutModalProps) {
  // El pago mixto solo tiene sentido si están activos efectivo y tarjeta.
  const cashEnabled = paymentMethods.some((m) => m.id === 'cash' && m.enabled);
  const cardEnabled = paymentMethods.some((m) => m.id === 'card' && m.enabled);
  const enabled = paymentMethods.filter((m) => {
    if (!m.enabled) return false;
    if (m.id === 'mixed') return cashEnabled && cardEnabled;
    return true;
  });

  const [method, setMethod] = useState<string>('cash');
  const [received, setReceived] = useState<string>('');
  const [cashPart, setCashPart] = useState<string>('');
  const [cardPart, setCardPart] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      const first = enabled[0]?.id ?? 'cash';
      setMethod(first);
      setReceived('');
      setCashPart('');
      setCardPart('');
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const isCash = method === 'cash';
  const isMixed = method === 'mixed';
  const receivedNum = received ? parseFloat(received) : 0;
  const change = isCash && receivedNum >= total ? round2(receivedNum - total) : 0;

  const cashNum = cashPart === '' ? 0 : parseFloat(cashPart) || 0;
  const cardNum = cardPart === '' ? 0 : parseFloat(cardPart) || 0;
  const mixedSum = round2(cashNum + cardNum);
  const mixedRemaining = round2(total - mixedSum);
  const mixedComplete = Math.abs(mixedRemaining) < 0.009;

  const selectMethod = (id: string) => {
    setMethod(id);
    setError(null);
    if (id === 'mixed') {
      setCashPart('');
      setCardPart(total.toFixed(2));
    }
  };

  const handleCashPart = (raw: string) => {
    setCashPart(raw);
    const n = raw === '' ? 0 : parseFloat(raw);
    if (Number.isNaN(n)) return;
    setCardPart(Math.max(0, round2(total - n)).toFixed(2));
  };

  const handleCardPart = (raw: string) => {
    setCardPart(raw);
    const n = raw === '' ? 0 : parseFloat(raw);
    if (Number.isNaN(n)) return;
    setCashPart(Math.max(0, round2(total - n)).toFixed(2));
  };

  const handleConfirm = async () => {
    setError(null);

    if (isCash && requireReceived) {
      if (!received || Number.isNaN(receivedNum)) {
        setError('Ingresa el monto recibido.');
        return;
      }
      if (receivedNum < total) {
        setError('El monto recibido es menor que el total.');
        return;
      }
    }

    if (isMixed) {
      if (Number.isNaN(cashNum) || Number.isNaN(cardNum)) {
        setError('Los montos del pago mixto no son válidos.');
        return;
      }
      if (cashNum <= 0 || cardNum <= 0) {
        setError('El pago mixto requiere un monto en efectivo y otro en tarjeta.');
        return;
      }
      if (!mixedComplete) {
        setError(
          `Los montos deben sumar el total. ${mixedRemaining > 0 ? 'Faltan' : 'Sobran'} ${formatMoney(
            Math.abs(mixedRemaining),
            currencySymbol
          )}.`
        );
        return;
      }
    }

    try {
      const receivedAmount = isMixed ? cashNum : isCash ? receivedNum : total;
      const changeAmount = isCash ? change : 0;
      await onConfirm(method, receivedAmount, changeAmount);
    } catch (err) {
      setError(friendlyError(err, 'No se pudo completar la venta.'));
    }
  };

  const selectedLabel = enabled.find((m) => m.id === method)?.name || PAYMENT_LABELS[method] || method;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Cobro"
      footer={
        <>
          <button
            onClick={onClose}
            disabled={processing}
            className="rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={processing}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition disabled:opacity-60 cursor-pointer"
          >
            <i className="ri-check-double-line" />
            {processing ? 'Procesando...' : 'FINALIZAR VENTA'}
          </button>
        </>
      }
    >
      <div className="space-y-5">
        <div className="rounded-lg bg-background-100 p-4 text-center">
          <p className="text-xs text-foreground-600">TOTAL A COBRAR</p>
          <p className="mt-1 text-3xl font-heading font-bold text-foreground-950">
            {formatMoney(total, currencySymbol)}
          </p>
        </div>

        <div>
          <p className="text-sm font-medium text-foreground-800 mb-2">Método de pago</p>
          <div className="grid grid-cols-2 gap-2">
            {enabled.map((m) => (
              <button
                key={m.id}
                onClick={() => selectMethod(m.id)}
                className={`inline-flex items-center justify-center gap-2 rounded-md border px-3 py-2.5 text-sm font-medium whitespace-nowrap transition cursor-pointer ${
                  method === m.id
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-background-300 text-foreground-700 hover:bg-background-100'
                }`}
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className={`${METHOD_ICONS[m.id] ?? 'ri-wallet-line'} text-base`} />
                </span>
                {m.name}
              </button>
            ))}
          </div>
        </div>

        {isCash && requireReceived && (
          <div>
            <label className="block text-sm font-medium text-foreground-800 mb-1.5">
              Monto recibido
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={received}
              onChange={(e) => setReceived(e.target.value)}
              placeholder="0.00"
              autoFocus
              className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
            {receivedNum >= total && (
              <p className="mt-2 text-sm font-medium text-accent-700">
                Cambio: {formatMoney(change, currencySymbol)}
              </p>
            )}
          </div>
        )}

        {isMixed && (
          <div className="rounded-lg border border-background-200 bg-background-100/60 p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground-900">
              <span className="w-7 h-7 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center">
                <i className="ri-wallet-3-line" />
              </span>
              Reparte el pago entre efectivo y tarjeta
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                  Efectivo
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-500">
                    {currencySymbol}
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={cashPart}
                    onChange={(e) => handleCashPart(e.target.value)}
                    placeholder="0.00"
                    autoFocus
                    className="w-full rounded-md border border-background-300 bg-background-50 pl-11 pr-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                  Tarjeta
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-500">
                    {currencySymbol}
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={cardPart}
                    onChange={(e) => handleCardPart(e.target.value)}
                    placeholder="0.00"
                    className="w-full rounded-md border border-background-300 bg-background-50 pl-11 pr-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between border-t border-background-200 pt-2.5 text-sm">
              <span className="text-foreground-600">Suma de montos</span>
              <span className="font-semibold text-foreground-950">
                {formatMoney(mixedSum, currencySymbol)}
              </span>
            </div>
            <p className={`text-xs font-medium ${mixedComplete ? 'text-accent-700' : 'text-primary-700'}`}>
              {mixedComplete
                ? 'Pago completo: los montos coinciden con el total.'
                : `${mixedRemaining > 0 ? 'Faltan' : 'Sobran'} ${formatMoney(
                    Math.abs(mixedRemaining),
                    currencySymbol
                  )} por asignar.`}
            </p>
          </div>
        )}

        <div className="flex items-center justify-between text-sm">
          <span className="text-foreground-600">Método seleccionado</span>
          <span className="font-medium text-foreground-900">{selectedLabel}</span>
        </div>

        {error && (
          <div className="rounded-lg border border-accent-200 bg-accent-50 px-3 py-2.5 text-sm text-foreground-700">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}